import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BROKERS = [
  { id: 'zerodha', name: 'Zerodha', logo: '🔷' },
  { id: 'angelone', name: 'AngelOne', logo: '🟠' },
  { id: 'dhan', name: 'Dhan', logo: '🟢' },
  { id: 'kotak_neo', name: 'Kotak Neo', logo: '🔵' },
  { id: 'master_trust', name: 'Master Trust', logo: 'Ⓜ️' }
];

const BrokerTab = () => {
  const [brokers, setBrokers] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedBroker, setSelectedBroker] = useState('');
  const [credentials, setCredentials] = useState({
    apiKey: '', // App ID for Master Trust
    apiSecret: '', // Secret Key
    clientId: '', // User ID (NDTT...)
    authCode: '', // New field for Auth Code
    redirectUri: 'http://127.0.0.1/' // Default Redirect URI
  });

  useEffect(() => {
    fetchBrokers();
  }, []);

  const fetchBrokers = async () => {
    try {
      const response = await axios.get(`${API}/brokers`, { withCredentials: true });
      setBrokers(response.data);
    } catch (error) {
      console.error('Failed to fetch brokers:', error);
    }
  };

  const handleCredentialChange = (e) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value
    });
  };

  const handleGetAuthCode = () => {
    if (!credentials.apiKey) {
      toast.error('Please enter OAuth Client ID first');
      return;
    }
    const baseUrl = 'https://masterswift-beta.mastertrust.co.in/oauth2/auth';
    const scope = 'orders';
    // Trim keys to avoid copy-paste whitespace issues
    const clientId = credentials.apiKey.trim();
    // Generate a random state string (at least 8 chars as per error requirement)
    const state = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    const url = `${baseUrl}?client_id=${clientId}&redirect_uri=${credentials.redirectUri}&scope=${scope}&response_type=code&state=${state}`;
    window.open(url, '_blank');
    toast.info('Please login and copy the "code" parameter from the redirected URL');
  };

  const connectBroker = async () => {
    if (!selectedBroker) {
      toast.error('Please select a broker');
      return;
    }

    // Basic validation
    if (selectedBroker === 'master_trust') {
      if (!credentials.clientId || !credentials.apiSecret || !credentials.apiKey || !credentials.authCode) {
        toast.error('Please fill in all Master Trust fields including Auth Code');
        return;
      }
    }

    try {
      await axios.post(`${API}/brokers/connect`, {
        broker_name: selectedBroker,
        // Map fields based on broker or generic
        client_id: credentials.clientId, // User ID (NDTT35280)
        api_key: credentials.apiKey || 'mock_key', // App ID
        api_secret: credentials.apiSecret || 'mock_secret',
        auth_code: credentials.authCode,
        redirect_uri: credentials.redirectUri
      }, { withCredentials: true });

      toast.success(`${BROKERS.find(b => b.id === selectedBroker)?.name} connected successfully!`);
      setShowDialog(false);
      setSelectedBroker('');
      setCredentials({ apiKey: '', apiSecret: '', clientId: '', authCode: '', redirectUri: 'http://127.0.0.1/' });
      fetchBrokers();
    } catch (error) {
      toast.error('Failed to connect broker: ' + (error.response?.data?.detail || error.message));
    }
  };

  const disconnectBroker = async (brokerId) => {
    const broker = brokers.find(b => b.id === brokerId);
    const brokerInfo = BROKERS.find(b => b.id === broker?.broker_name);

    if (!window.confirm(`Are you sure you want to disconnect ${brokerInfo?.name || 'this broker'}? You will need to reconnect to deploy strategies.`)) return;

    try {
      await axios.delete(`${API}/brokers/${brokerId}`, { withCredentials: true });
      toast.success(`${brokerInfo?.name || 'Broker'} disconnected successfully`);

      // Remove from list immediately
      setBrokers(prevBrokers => prevBrokers.filter(b => b.id !== brokerId));

      // Refresh to sync
      setTimeout(fetchBrokers, 500);
    } catch (error) {
      console.error('Disconnect broker error:', error);
      toast.error(error.response?.data?.detail || 'Failed to disconnect broker');
    }
  };

  return (
    <Card className="p-8 bg-white/80 backdrop-blur-sm border-gray-200" data-testid="broker-tab-content">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Broker Integration
        </h2>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="connect-broker-btn">
              <Plus className="w-4 h-4 mr-2" />
              Connect Broker
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Connect Broker</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <Select value={selectedBroker} onValueChange={setSelectedBroker}>
                <SelectTrigger data-testid="broker-select">
                  <SelectValue placeholder="Select broker" />
                </SelectTrigger>
                <SelectContent>
                  {BROKERS.filter(b => !brokers.some(connected => connected.broker_name === b.id && connected.is_connected)).map((broker) => (
                    <SelectItem key={broker.id} value={broker.id}>
                      <span className="flex items-center gap-2">
                        <span>{broker.logo}</span>
                        {broker.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {selectedBroker === 'master_trust' && (
                <div className="space-y-3">
                  <div className="grid gap-2">
                    <label className="text-sm font-medium">Trading Client Code (e.g. NDTT35280)</label>
                    <input
                      name="clientId"
                      value={credentials.clientId}
                      onChange={handleCredentialChange}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      placeholder="Trading Account ID"
                    />
                  </div>
                  <div className="grid gap-2">
                    <label className="text-sm font-medium">OAuth Client ID (App Key)</label>
                    <input
                      name="apiKey"
                      value={credentials.apiKey}
                      onChange={handleCredentialChange}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      placeholder="e.g. bIMejtizWJ"
                    />
                  </div>
                  <div className="grid gap-2">
                    <label className="text-sm font-medium">OAuth Secret Key</label>
                    <input
                      name="apiSecret"
                      type="password"
                      value={credentials.apiSecret}
                      onChange={handleCredentialChange}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      placeholder="Enter Secret Key"
                    />
                  </div>

                  <div className="pt-2 border-t border-gray-100">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">OAuth Authorization</label>
                    <div className="mt-2 flex gap-2 items-end">
                      <div className="flex-1 grid gap-2">
                        <label className="text-sm font-medium">Authorization Code</label>
                        <input
                          name="authCode"
                          value={credentials.authCode}
                          onChange={handleCredentialChange}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          placeholder="Paste code from redirected URL"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleGetAuthCode}
                        title="Open Login Page"
                      >
                        Get Code
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Click 'Get Code' &rarr; Login &rarr; Copy 'code=...' from URL &rarr; Paste above.
                    </p>
                  </div>
                </div>
              )}

              {(selectedBroker && selectedBroker !== 'master_trust') && (
                <p className="text-sm text-gray-600">
                  Note: This is a mock connection. In production, you would enter real API credentials.
                </p>
              )}

              <Button onClick={connectBroker} className="w-full" data-testid="confirm-connect-btn">
                Connect
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {brokers.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 mb-4">No brokers connected yet</p>
          <p className="text-sm text-gray-400">Connect a broker to start deploying strategies</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {brokers.map((broker) => {
            const brokerInfo = BROKERS.find(b => b.id === broker.broker_name);
            return (
              <div key={broker.id} className="p-4 bg-slate-50 rounded-xl border border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{brokerInfo?.logo || '📊'}</span>
                  <div>
                    <h3 className="font-semibold text-gray-900">{brokerInfo?.name || broker.broker_name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      {broker.is_connected ? (
                        <>
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span className="text-sm text-green-600">Connected</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="w-4 h-4 text-red-600" />
                          <span className="text-sm text-red-600">Disconnected</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <Button
                  onClick={() => disconnectBroker(broker.id)}
                  variant="destructive"
                  size="sm"
                  data-testid={`disconnect-btn-${broker.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

export default BrokerTab;
